import { MrfiktivHttpClientProvider } from "@/services/mrfiktiv/mrfiktiv-http-client.provider";
import {
  MrfiktivCreateInvoicePaymentDtoGen,
  MrfiktivCreateOutboundInvoiceDocumentDtoGen,
  MrfiktivInvoiceControllerCreatePayloadGen,
  MrfiktivInvoiceControllerGetParamsGen,
  MrfiktivInvoiceControllerUpdatePayloadGen
} from "../v1/data-contracts";
import { Invoice } from "../v1/Invoice";

/**
 * Communicate with invoice API
 */
class InvoiceService {
  mrfiktivProxy: Invoice;

  /**
   * Construct service
   */
  constructor(mrfiktivHttpClientProvider: MrfiktivHttpClientProvider) {
    this.mrfiktivProxy = new Invoice(mrfiktivHttpClientProvider.client());
  }

  /**
   * Create aninvoice
   */
  async create(partnerId: string, data: MrfiktivInvoiceControllerCreatePayloadGen) {
    const invoice = (await this.mrfiktivProxy.invoiceControllerCreate(partnerId, data)).data;

    return invoice;
  }

  /**
   * Get allinvoice
   */
  async get(data: MrfiktivInvoiceControllerGetParamsGen) {
    const Invoices = (await this.mrfiktivProxy.invoiceControllerGet(data)).data;

    return Invoices;
  }

  /**
   * Get an invoice
   */
  async getOne(partnerId: string, invoiceId: string) {
    const invoice = (await this.mrfiktivProxy.invoiceControllerGetOne(partnerId, invoiceId)).data;

    return invoice;
  }

  /**
   * Update an invoice
   */
  async update(partnerId: string, invoiceId: string, data: MrfiktivInvoiceControllerUpdatePayloadGen) {
    const invoice = (await this.mrfiktivProxy.invoiceControllerUpdate(partnerId, invoiceId, data)).data;

    return invoice;
  }

  /**
   * Delete an invoice
   */
  async delete(partnerId: string, invoiceId: string) {
    const invoice = (await this.mrfiktivProxy.invoiceControllerDelete(partnerId, invoiceId)).data;

    return invoice;
  }

  /**
   * Print the invoice. Set status to created.
   * @param partnerId
   * @param invoiceId
   * @param zugferd embed the X-Invoice XML into the PDF (ZUGFeRD)
   * @returns
   */
  async print(partnerId: string, invoiceId: string, zugferd?: boolean) {
    const invoice = (await this.mrfiktivProxy.invoiceControllerCreatePdf({ partnerId, invoiceId, zugferd })).data;

    return invoice;
  }

  /**
   * Mark the invoice as sent to the buyer.
   * @param partnerId
   * @param invoiceId
   * @returns
   */
  async send(partnerId: string, invoiceId: string) {
    const invoice = (await this.mrfiktivProxy.invoiceControllerSend(partnerId, invoiceId)).data;

    return invoice;
  }

  /**
   * Record a payment on an outbound invoice.
   * @param partnerId
   * @param invoiceId
   * @param data
   * @returns
   */
  async addPayment(partnerId: string, invoiceId: string, data: MrfiktivCreateInvoicePaymentDtoGen) {
    const invoice = (await this.mrfiktivProxy.invoiceControllerAddPayment(partnerId, invoiceId, data)).data;

    return invoice;
  }

  /**
   * Check whether an inbound invoice with the same number already exists for a supplier.
   * @param partnerId
   * @param number
   * @param companyId
   * @param excludeInvoiceId
   * @returns
   */
  async checkDuplicate(partnerId: string, number: string, companyId: string, excludeInvoiceId?: string) {
    const result = (
      await this.mrfiktivProxy.invoiceControllerCheckDuplicate({ partnerId, number, companyId, excludeInvoiceId })
    ).data;

    return result;
  }

  /**
   * Add document to the invoice. Set status to created.
   * @param partnerId
   * @param invoiceId
   * @param data
   * @returns
   */
  async addDocument(partnerId: string, invoiceId: string, data: MrfiktivCreateOutboundInvoiceDocumentDtoGen) {
    const invoice = (await this.mrfiktivProxy.invoiceControllerAddDocument(partnerId, invoiceId, data)).data;

    return invoice;
  }

  /**
   * Preview the PDF of the invoice
   * @param partnerId
   * @param invoiceId
   * @param zugferd embed the X-Invoice XML into the PDF (ZUGFeRD)
   * @returns
   */
  async generatePdfPreview(partnerId: string, invoiceId: string, zugferd?: boolean): Promise<Uint8Array> {
    const bufferObject = (await this.mrfiktivProxy.invoiceControllerGetPdf({ partnerId, invoiceId, zugferd })).data;

    return new Uint8Array(Object.values(bufferObject) as number[]);
  }
}

/**
 * Communicate with invoice API
 */
export default new InvoiceService(new MrfiktivHttpClientProvider());
